import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";

import { logError } from "./logging";

/* iOS-aware adapter over the auth-token blob. The blob itself is a JSON
 * string holding { accessToken, refreshToken, email, expiresAt } — see
 * helpers/auth.js#getLocalStorageToken/setLocalStorageToken for the shape.
 *
 * Web + Android use localStorage directly (no behavioural change vs the
 * previous code). iOS Capacitor routes through NSUserDefaults via
 * @capacitor/preferences so the blob survives WKWebView's purgeable-
 * localStorage eviction. See docs/capacitor-ios-plan.md "Note on the
 * persistent-storage package choice" for the full rationale (we
 * considered Keychain via @aparajita/capacitor-secure-storage and
 * opted out — official-plugin simplicity + parity with RN's
 * AsyncStorage posture outweighed the marginal backup-protection
 * upgrade Keychain would have offered).
 *
 * Sync surface. Apollo's token refresh link (apollo-link-token-refresh)
 * reads the cached token inside a setContext() callback that doesn't
 * await, and there are ~14 other call sites scattered across login
 * forms and auth.js that all assume synchronous access. Making the
 * read async would cascade through every link in the GraphQL client.
 *
 * To keep callers sync we maintain an in-memory cache:
 *   • App boot — capacitorBootstrap.ts calls `loadTokenIntoCache()` and
 *     awaits it before mounting React, so the first render sees the
 *     correct value.
 *   • Read — `readAuthToken()` returns the cache on iOS, falls through
 *     to `localStorage.getItem` on web + Android (where localStorage is
 *     the live source of truth).
 *   • Write — `writeAuthToken(value)` updates the cache synchronously
 *     and fires the persistent write off without awaiting. Preferences
 *     writes are fast and idempotent; if one fails the cache stays
 *     consistent until the next session.
 */

const KEY = import.meta.env.REACT_APP_AUTH_LOCAL_STORAGE;
const isIOSCapacitor = Capacitor.getPlatform() === "ios";

let cachedToken: string | null = null;

/* Awaited from capacitorBootstrap.ts at app start, before React mounts.
 * On iOS pulls the token from NSUserDefaults into the in-memory cache.
 * On other platforms it's effectively a no-op — `readAuthToken()` reads
 * localStorage directly. */
export const loadTokenIntoCache = async (): Promise<void> => {
  if (!isIOSCapacitor) return;
  try {
    const { value } = await Preferences.get({ key: KEY });
    cachedToken = value ?? null;
  } catch (error) {
    logError("loadTokenIntoCache: Preferences read failed", { error });
    cachedToken = null;
  }
};

export const readAuthToken = (): string | null => {
  if (!isIOSCapacitor) {
    return localStorage.getItem(KEY);
  }
  return cachedToken;
};

export const writeAuthToken = (value: string | null): void => {
  cachedToken = value;
  if (!isIOSCapacitor) {
    if (value == null) {
      localStorage.removeItem(KEY);
    } else {
      localStorage.setItem(KEY, value);
    }
    return;
  }
  if (value == null) {
    Preferences.remove({ key: KEY }).catch((error) => {
      logError("writeAuthToken: Preferences remove failed", { error });
    });
    return;
  }
  Preferences.set({ key: KEY, value }).catch((error) => {
    logError("writeAuthToken: Preferences set failed", { error });
  });
};
